/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import { gettext } from '../../utils/text';
import RadioButton from '../RadioButton';
import UploadWorkMedia from './UploadWorkMedia';
import UploadWorkMediaPreview from './UploadWorkMediaPreview';
import api from '../../utils/api';
import CSRFToken from '../CSRFToken';
import { ErrorsShape, SelectOptionsShape } from '../../utils/shapes';
import NonFieldErrors from '../inputs/NonFieldErrors';
import FieldErrors from '../inputs/FieldErrors';
import UploadWorkText from './UploadWorkText';

class UploadWork extends React.Component {
    static propTypes = {
        accept: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        textAlignOptions: SelectOptionsShape.isRequired,
        textFontOptions: SelectOptionsShape.isRequired,
        textLineheightOptions: SelectOptionsShape.isRequired,
        initialValues: PropTypes.shape({
            title: PropTypes.string,
            comment: PropTypes.string,
            workType: PropTypes.string,
        }),
        errors: ErrorsShape,
    };

    static defaultProps = {
        accept: undefined,
        initialValues: {},
        errors: {},
    };

    static emptyState = {
        values: {
            title: '',
            comment: '',
            workType: 'media',
            media: null,

            text: '',
            text_align: 'left',
            text_font: 'sans-small',
            text_lineheight: 'medium',
        },
        file: null,
        filesAreBeingUploaded: false,
        fileIsBeingDeleted: false,
        previewingText: false,
        errorMessage: null,
    };

    constructor(props) {
        super(props);
        const initialState = {
            ...UploadWork.emptyState,
        };
        if (Object.keys(props.initialValues).length > 0) {
            initialState.values = {
                ...initialState.values,
                ...props.initialValues,
            };
        }

        this.state = initialState;
    }

    componentWillUnmount() {
        // Revoke preview to avoid memory leaks
        if (this.state.file) {
            window.URL.revokeObjectURL(this.state.file);
        }
    }

    onDrop(acceptedFiles, rejectedFiles) {
        if (acceptedFiles.length > 0) {
            const acceptedFile = acceptedFiles[0];
            this.uploadFile(acceptedFile);
        }
        if (acceptedFiles.length > 1) {
            this.setState({
                errorMessage: gettext('You can only upload one work at a time.'),
            });
        }
        if (rejectedFiles.length > 0) {
            this.setState({
                errorMessage: gettext('Some of the file(s) you uploaded were rejected because of their type'),
            });
        }
    }

    deleteMedia() {
        if (!this.state.values.media || this.state.fileIsBeingDeleted) return;
        if (this.state.file) {
            window.URL.revokeObjectURL(this.state.file);
        }
        this.setState({
            fileIsBeingDeleted: true,
        });

        api.mediaDelete
            .post({ pk: this.state.values.media.id })
            .then(() => {
                this.setState({
                    ...UploadWork.emptyState,
                    values: {
                        ...UploadWork.emptyState.values,
                        workType: this.state.values.workType, // Keep selected workType
                    },
                });
            })
            .finally(() => {
                this.setState({ fileIsBeingDeleted: false });
            });
    }

    handleChange(e) {
        const name = e.target.name;
        const value = e.target.value;
        this.setState((currentState) => ({
            values: {
                ...currentState.values,
                [name]: value,
            },
        }));
    }

    toggleTextPreview() {
        this.setState((currentState) => ({
            previewingText: !currentState.previewingText,
        }));
    }

    uploadFile(file) {
        this.setState({
            filesAreBeingUploaded: true,
        });
        const data = {
            unpublished: true,
        };
        const attachment = {
            field: 'files',
            file,
            name: file.name,
        };
        api.mediaUploadPrivate
            .post(null, data, null, [attachment])
            .then((result) => {
                const response = typeof result === 'string' ? JSON.parse(result) : result;
                if (response.success) {
                    const mediaItem = response.uploads[0];
                    this.setState((currentState) => ({
                        file,
                        values: {
                            ...currentState.values,
                            media: mediaItem,
                        },
                    }));
                } else {
                    const name = file.name;
                    const reason = result.error ? result.error : '';
                    this.setState({
                        errorMessage: gettext(`File upload failed for file ${name}. ${reason}`),
                    });
                }
            })
            .finally(() => {
                this.setState({
                    filesAreBeingUploaded: false,
                });
            });
    }

    canSubmit() {
        if (this.state.values.workType === 'media') {
            if (!this.state.values.media) return false;
        }
        return true;
    }

    render() {
        let content = '';
        if (this.state.values.workType === 'media') {
            if (this.state.file || this.state.values.media) {
                content = (
                    <UploadWorkMediaPreview
                        file={this.state.file}
                        media={this.state.values.media}
                        title={this.state.values.title}
                        comment={this.state.values.comment}
                        errors={this.props.errors}
                        handleChange={(e) => this.handleChange(e)}
                        onDelete={() => this.deleteMedia()}
                    />
                );
            } else {
                content = (
                    <UploadWorkMedia
                        filesAreBeingUploaded={this.state.filesAreBeingUploaded}
                        accept={this.props.accept}
                        onDrop={(...args) => this.onDrop(...args)}
                    />
                );
            }
        } else if (this.state.values.workType === 'text') {
            content = (
                <UploadWorkText
                    values={this.state.values}
                    errors={this.props.errors}
                    previewingText={this.state.previewingText}
                    textAlignOptions={this.props.textAlignOptions}
                    textFontOptions={this.props.textFontOptions}
                    textLineheightOptions={this.props.textLineheightOptions}
                    handleChange={(e) => this.handleChange(e)}
                    toggleTextPreview={() => this.toggleTextPreview()}
                />
            );
        }
        return (
            <div className="upload-work">
                <form method="post" noValidate="noValidate">
                    <CSRFToken />
                    <div className="mb-3">
                        <RadioButton
                            id="radio-work-type-media"
                            name="workType"
                            value="media"
                            label={gettext('Media')}
                            checked={this.state.values.workType === 'media'}
                            onChange={(e) => this.handleChange(e)}
                        />
                        <RadioButton
                            id="radio-work-type-text"
                            name="workType"
                            value="text"
                            label={gettext('Text')}
                            checked={this.state.values.workType === 'text'}
                            onChange={(e) => this.handleChange(e)}
                        />
                    </div>
                    <FieldErrors error={this.props.errors.workType} />

                    {content}

                    {this.state.errorMessage && <div className="alert alert-danger">{this.state.errorMessage}</div>}
                    <NonFieldErrors error={this.props.errors.__all__} />
                    <button className="btn btn-primary mt-4" type="submit" disabled={!this.canSubmit()}>
                        {gettext('Upload work')}
                    </button>
                </form>
            </div>
        );
    }
}

export default UploadWork;



// WEBPACK FOOTER //
// ./src/components/arena/UploadWork.js